import { useState, useRef, useEffect } from "react";
import arrowRight from "../../../Assets/images/arrow-right.svg";
import { checkOTP } from "../../../Services/settingsServices";
import { resendOTP } from "../../../Services/settingsServices";
import Loader from "react-js-loader";

function PhoneSettingsScreen3({ onVerifyOTP, onChangePhoneNumber }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false); // Track if button clicked
  const [errorMessage, setErrorMessage] = useState(""); // For error messages

  const length = 6;
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 3000); // 3000 ms = 3 seconds

      return () => clearTimeout(timer); // cleanup on unmount or error change
    }
  }, [errorMessage]);

  const handleChange = (value, index) => {
    if (/^[A-Za-z0-9]$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData
      .getData("text")
      .split("")
      .slice(0, length);
    const newOtp = [...otp];

    pasteData.forEach((char, i) => {
      if (/^[A-Za-z0-9]$/.test(char)) {
        newOtp[i] = char;
      }
    });

    setOtp(newOtp);

    inputRefs.current[pasteData.length - 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    const key = e.key;
    // Overwrite behavior for alphanumeric keys
    if (/^[A-Za-z0-9]$/.test(key)) {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = key;
      setOtp(newOtp);
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }

    if (key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
      return;
    }
  };

  const onVerifyOtpClick = async () => {
    if (isButtonClicked) return;
    const code = otp.join("");
    if (code.length < length) {
      setErrorMessage("Please enter the 6-digit code.");
      return;
    }
    setIsButtonClicked(true);
    setIsLoading(true);
    const countryCode = sessionStorage.getItem("newcountryCode");
    const contact = sessionStorage.getItem("newContact");

    try {
      const response = await checkOTP({
        contact: contact,
        country_code: countryCode,
        otp: String(code),
      });

      const payload = response?.payload;
      if (payload?.success || response?.status === "success") {
        onVerifyOTP();
        sessionStorage.removeItem("newcountryCode");
        sessionStorage.removeItem("newContact");
      } else if (response?.status === 422) {
        setErrorMessage(response?.message || response?.mesage || "Invalid code. Please try again.");
      } else if (response?.message || response?.mesage) {
        setErrorMessage(response.message || response.mesage);
      } else {
        setErrorMessage("Verification failed. Please try again.");
      }
    } catch (error) {
      console.error("[PhonePopupVerify] checkOTP error:", error);
      const apiMessage = error?.response?.data?.message || error?.response?.data?.mesage || error?.message;
      const apiStatus = error?.response?.status || error?.response?.data?.status;
      if (apiStatus === 422) {
        setErrorMessage(apiMessage || "Invalid code. Please try again.");
      } else if (apiMessage) {
        setErrorMessage(apiMessage);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setIsButtonClicked(false);
    }
  };

  const onResendOTP = async () => {
    setIsLoading(true);
    const countryCode = sessionStorage.getItem("newcountryCode");
    const contact = sessionStorage.getItem("newContact");
    try {
      const response = await resendOTP({
        country_code: countryCode,
        contact: contact,
      });
      if (response.status === "success") {
        setErrorMessage("");
      } else if (response?.status === 422) {
        setErrorMessage(response?.message || response?.mesage || "Unable to resend code. Please try again later.");
      }
    } catch (error) {
      console.error("[PhonePopupVerify] resendOTP error:", error);
      const apiMessage = error?.response?.data?.message || error?.response?.data?.mesage || error?.message;
      const apiStatus = error?.response?.status || error?.response?.data?.status;
      if (apiStatus === 422) {
        setErrorMessage(apiMessage || "Unable to resend code. Please try again later.");
      } else if (apiMessage) {
        setErrorMessage(apiMessage);
      } else {
        setErrorMessage("Unable to resend code. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="set-phone-modal-container">
      {(isLoading) && (
        <div className="loader-df-container">
          <Loader
            type="spinner-cub"
            bgColor={"#2D50A7"}
            color={"#3D6DEB"}
            // title={"spinner-cub"}
            size={45}
          />
        </div>
      )}
      <div>
        <div className="set-phone-modal-head">Verify your mobile number</div>
        <div className="set-phone-modal-support">
          We sent a six-digit confirmation code to your number. It will expire
          in 30 minutes, enter it soon! Keep the window open while checking for
          your code.
        </div>
      </div>
      <div className="set-phone-int-cnt">
        <div className="otp-verifier">
          <div className="otp-container" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => (inputRefs.current[index] = el)}
                className="otp-box"
              />
            ))}
          </div>
          <div className="otp-rote">
            <div className="otp-typo-cnt">
              <div className="otp-get-code">Didn’t get a code?</div>
              <div
                className="otp-resend"
                onClick={(e) => {
                  e.stopPropagation();
                  onResendOTP();
                }}
                style={{
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              >
                Resend code
              </div>
            </div>
            <div
              className="otp-resend"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onChangePhoneNumber(); // Trigger change phone number function
              }}
            >
              Change mobile number
            </div>
          </div>
        </div>
        {errorMessage && (
          <div
            style={{ color: "red", textAlign: "center", marginBottom: "10px" }}
          >
            {errorMessage}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
            marginTop: "20px",
          }}
        >
          <button
            className="set-phone-continue-button"
            onClick={(e) => {
              e.stopPropagation();
              onVerifyOtpClick();
            }}
            type="button"
            disabled={isLoading}
          >
            Continue <img src={arrowRight} alt="edit" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhoneSettingsScreen3;
